import React from 'react';
import { CircleDrawIcon, HandIcon, PolygonDrawIcon } from 'assets/icons';
import { DrawingController, DrawingMode } from '../hooks/useDrawingManager';

interface CustomDrawingControlsProps {
  controller: DrawingController | null;
  activeMode: DrawingMode;
  hasCurrentFeature: boolean;
}

const CustomDrawingControls: React.FC<CustomDrawingControlsProps> = ({ controller, activeMode, hasCurrentFeature }) => {
  const handleModeChange = (mode: DrawingMode) => {
    if (!controller) return;
    controller.setMode(mode);
  };

  const getIdleMode = (): DrawingMode => (hasCurrentFeature ? 'select' : 'static');

  const handleToggleMode = (mode: DrawingMode) => {
    handleModeChange(activeMode === mode ? getIdleMode() : mode);
  };

  // Pan ↔ edit: with a shape, hand toggles static (pan) and select (edit).
  // Leaving draw tools with a shape returns to select so vertices stay editable.
  const handlePanClick = () => {
    if (activeMode === 'polygon' || activeMode === 'circle') {
      handleModeChange(getIdleMode());
      return;
    }

    if (!hasCurrentFeature) {
      handleModeChange('static');
      return;
    }

    handleModeChange(activeMode === 'select' ? 'static' : 'select');
  };

  return (
    <div className="flex gap-1 overflow-hidden p-2">
      <div
        className={`cursor-pointer rounded-sm border bg-white p-2 hover:border-secondary ${
          activeMode === 'static' || activeMode === 'select' ? 'border-secondary' : 'border-borderGray'
        }`}
        role="button"
        tabIndex={0}
        aria-label={hasCurrentFeature && activeMode === 'static' ? 'Edit shape mode' : 'Pan mode'}
        onClick={handlePanClick}
        onKeyDown={event => {
          if (event.key === 'Enter' || event.key === ' ') {
            event.preventDefault();
            handlePanClick();
          }
        }}>
        <HandIcon
          className={`path path-fill-current ${
            activeMode === 'static' || activeMode === 'select' ? 'text-secondary' : ''
          }`}
        />
      </div>
      <div
        className={`cursor-pointer rounded-sm border bg-white p-2 hover:border-secondary ${
          activeMode === 'polygon' ? 'border-secondary' : 'border-borderGray'
        }`}
        role="button"
        tabIndex={0}
        aria-label="Polygon draw mode"
        onClick={() => handleToggleMode('polygon')}
        onKeyDown={event => {
          if (event.key === 'Enter' || event.key === ' ') {
            event.preventDefault();
            handleToggleMode('polygon');
          }
        }}>
        <PolygonDrawIcon className={`path path-fill-current ${activeMode === 'polygon' ? 'text-secondary' : ''}`} />
      </div>
      <div
        className={`cursor-pointer rounded-sm border bg-white p-2 hover:border-secondary ${
          activeMode === 'circle' ? 'border-secondary' : 'border-borderGray'
        }`}
        role="button"
        tabIndex={0}
        aria-label="Circle draw mode"
        onClick={() => handleToggleMode('circle')}
        onKeyDown={event => {
          if (event.key === 'Enter' || event.key === ' ') {
            event.preventDefault();
            handleToggleMode('circle');
          }
        }}>
        <CircleDrawIcon className={`path path-fill-current ${activeMode === 'circle' ? 'text-secondary' : ''}`} />
      </div>
    </div>
  );
};

export default CustomDrawingControls;
